"use client";

import Link from "next/link";
import { LockKeyhole } from "lucide-react";
import { LanguageSelector } from "@/components/i18n/LanguageSelector";
import { LogoMark } from "@/components/ui/logo";
import { localePath } from "@/lib/i18n/locales";
import { useLocale, useT } from "@/lib/use-i18n";

interface AuthLayoutProps {
  children: React.ReactNode;
}

export function AuthLayout({ children }: AuthLayoutProps) {
  const t = useT();
  const locale = useLocale();
  const lp = (path: string) => localePath(locale, path);

  return (
    // 2026-09-26 auth sin scroll: 100dvh fijo + overflow-hidden — todo el
    // formulario cabe en un solo frame (título compacto + espaciados
    // reducidos en AuthForm; ver nota ahí). Sin min-h-screen para no
    // generar scroll vertical en viewports normales.
    <div className="relative flex h-[100dvh] w-full flex-col overflow-hidden bg-background text-foreground antialiased">
      {/* 2026-10-23 Auth v2 (dueño: la ola magenta ensuciaba las cards y
          competía con el formulario): ambiente monocromo — un halo superior
          neutro + retícula muy tenue, 0 JS, 0 animación (respeta
          prefers-reduced-motion por construcción y el budget free-tier CPU).
          Reemplaza HeroAmbientCanvas + PageAmbient (azul/violeta/rosa). */}
      <div className="auth-ambient" aria-hidden="true" />

      <div className="relative z-[1] mx-auto flex h-full w-full max-w-[440px] flex-col px-4 py-2 sm:px-4">
        {/* Top Bar: Brand Logo & Language Switcher */}
        {/* 2026-09-12: wrap + min-w-0 — a 280px logo+Pricing+idioma sumaban ~260px > 256px útiles. */}
        {/* 2026-09-26 sin scroll: pb-4 → pb-2 para ganar 8px verticales. */}
        <div className="flex min-w-0 w-full flex-wrap items-center justify-between gap-2 pt-1 pb-2">
          <Link
            href={lp("/")}
            className="group flex items-center gap-2.5 transition-opacity hover:opacity-90"
            aria-label={t("navBack")}
          >
            <LogoMark
              size={30}
              className="transition-transform duration-200 group-hover:scale-105"
            />
            {/* 2026-10-23 Auth v2: tamaño explícito para que el wordmark tenga
                la misma escala en login y signup, sin depender de una utility
                ambigua con una variable CSS. */}
            <span className="text-[17px] font-bold tracking-normal text-foreground">
              vortixy
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              href={lp("/pricing")}
              className="text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t("navPricing")}
            </Link>
            <LanguageSelector />
          </div>
        </div>

        {/* Center Content (Auth Form) — sin scroll: min-h-0 + justify-center
            compacto; sin pb-16/py-8 que empujaba el contenido fuera del frame.
            2026-09-26 RUN2 FASE3 (SEED=2026-09-26-e8851c1e, WCAG 1.4.10
            reflow): overflow-y-auto SOLO como válvula de escape — en
            landscape bajo / zoom 200% el frame 100dvh+hidden recortaba el
            submit sin remedio; con contenido que cabe no hay scroll (el
            contenedor no excede), con overflow aparece solo el necesario. */}
        <main
          id="main"
          className="flex min-h-0 flex-1 flex-col justify-center overflow-y-auto py-1"
        >
          {children}
        </main>

        {/* Bottom Security / SSL Footnote + ayuda consistente */}
        {/* 2026-09-22 FASE 3 FIX P2 a11y 3.2.6 Consistent Help + P3
            contraste 11px (research 2026 WCAG 2.2: muted-foreground #b0b0c8
            sobre charcoal 4.6:1 / #57534e sobre warm-paper ~7:1 — AA 4.5:1
            verificado en ambos temas; 11px solo para footnote legal, no body). */}
        <div className="border-t border-border pt-2 pb-1 text-center">
          {/* 2026-09-24 FASE 7 tipografía: footnote legal 11px FIJO
            (antes fluido sm:text-[12px] que crecía en desktop; 11px solo
            para footnote legal, no body — AA verificado en comentarios). */}
          <p className="flex items-center justify-center gap-1.5 text-[11px] font-normal text-muted-foreground">
            <LockKeyhole className="size-3 shrink-0" aria-hidden="true" />
            <span>{t("authSecurityNotice")}</span>
            <span aria-hidden="true">·</span>
            <Link
              href={lp("/contact")}
              className="underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"
            >
              {t("docsHelpContact")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
